import { MouseEventHandler, useMemo } from 'react'
import parser, { DOMNode, Element } from 'html-react-parser'
import styled from 'styled-components'
import { match } from 'ts-pattern'
import { RouletteContent } from '@/components/domains/events/roulette-content'
import { ENV_CONFIG } from '@/constants/env/config'
import ROUTES, { checkAuthRoute } from '@/constants/legacy/constRoutes'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { EventContainer } from '@/containers/contexts/regional/event.container'
import useEventDetailEventTracker from '@/containers/event-tracker/event-detail.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import { hasSignedIn } from '@/containers/hooks/useUserInfo'
import { Bridge } from '@/utils/bridge/bridge'
import appBridgeProvider, { appBridgeLogin, isInAppFlag } from '@/utils/utilBridge'
import { openExternalUrl, openExternalWithBridgeUrl } from '@/utils/utilURL'

const CUSTOM_HOST = 'http://192.168.21.118:4000'

const EventDetailDescription = () => {
  const { ad } = EventContainer.useContainer()
  const { trackClickEventsaleDetailImage } = useEventDetailEventTracker()
  const { push } = useCustomRouter()
  const bridgeLogin = appBridgeLogin()

  const sendTrackingEvent = (adUrl: string) => {
    trackClickEventsaleDetailImage({
      adId: ad.id,
      adSlug: ad.slug ?? '',
      adName: ad.name,
      adUrl,
    })
  }

  /**
   * 리뷰 리스트 페이지로 이동하는 경우 앱/웹에서 로그인 처리를 필요
   * 이미지 링크가 mypage가 아닌 경우에는 로그인 없이 이미지의 링크로 이동
   * @TODO: EventDetailContentLegacy.tsx의 코드를 옮겨온 것으로 불필요한 로직으로 확인되는 부분은 삭제처리 필요
   */
  const handleClick: MouseEventHandler<HTMLElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()

    let el: HTMLElement | null = e.target as HTMLElement
    while (el && el !== e.currentTarget && el.tagName !== 'A') {
      el = el.parentNode as HTMLElement
    }
    if (el && el.tagName !== 'A') {
      return
    }

    const targetLink = el?.getAttribute('href')

    if (!targetLink) {
      return
    }

    // 앰플리튜드 이벤트 트래킹
    sendTrackingEvent(targetLink)

    // kakao app link 처리
    const webProtocols = ['http:', 'https:']
    const isAppLink = !webProtocols.includes(new URL(targetLink).protocol)
    if (isAppLink && isInAppFlag) {
      openExternalWithBridgeUrl(targetLink)
      return
    }

    // BLOG: 'https://fitpetmall.com/blog',
    // BLOG_WWW: 'https://www.fitpetmall.com/blog',
    // blog 이동 url 처리
    if (targetLink.startsWith(ROUTES.BLOG) || targetLink.startsWith(ROUTES.BLOG_WWW)) {
      return window.open(targetLink)
    }

    if (!targetLink.startsWith(ENV_CONFIG.fitpetmallHost) && !targetLink.startsWith(CUSTOM_HOST)) {
      return openExternalUrl(targetLink)
    }
    const getPathname = (url: string) => {
      if (url.startsWith(ENV_CONFIG.fitpetmallHost) || url.startsWith(CUSTOM_HOST)) {
        return url.replace(ENV_CONFIG.fitpetmallHost, '').replace(CUSTOM_HOST, '').replace('/mall', '')
      }
      return ''
    }

    const deepLinkList: {
      [key: string]: { value: [string, string]; isAuthRoute: boolean }
    } = {
      '/': { value: ['fitpetmall', '/main'], isAuthRoute: false },
      '/mypage': { value: ['mypage', '/main'], isAuthRoute: true },
      '/mypage/like-list': { value: ['fitpetmall', '/detail/mypage/like-list'], isAuthRoute: false },
      '/mypage/recently-list': { value: ['fitpetmall', '/detail/mypage/recently-list'], isAuthRoute: true },
      '/home/events': { value: ['fitpetmall', '/home/events'], isAuthRoute: false },
    }

    const returnUrl = new URL(targetLink).searchParams.get('return_url') || getPathname(targetLink)
    const deepLink = deepLinkList[returnUrl]
    const isDeepLink = Object.keys(deepLinkList).includes(returnUrl) // 네이티브 화면 여부 ts-mattern에서 사용하기 위해 정의
    const isAuthRoute = checkAuthRoute(targetLink) // 로그인 필요한 페이지 여부
    const isLogin = hasSignedIn() // 로그인 여부

    /*
    @example
      만약 isInAppFlag이 true이면:
      - 사용자가 로그인하고 딥 링크 플래그가 true인 경우:
        - appBridgeProvider를 사용하여 브릿지를 얻어와서 deepLinkData.deeplinkValue를 사용하여 goDeepLink를 실행합니다.
      - 사용자가 로그인하고 딥 링크 플래그가 false인 경우:
      ...
*/
    if (isInAppFlag) {
      match({ isLogin, isAuthRoute, isDeepLink })
        .with({ isLogin: true, isDeepLink: true }, () =>
          appBridgeProvider((bridge: Bridge) => {
            return bridge.goDeepLink(...deepLink.value)
          })
        )
        .with({ isLogin: true, isDeepLink: false }, () => push(targetLink))
        .with({ isLogin: false, isDeepLink: true, isAuthRoute: true }, () =>
          appBridgeProvider((bridge: Bridge) =>
            bridge.login().then(() => {
              bridgeLogin(window.getToken())
              return bridge.goDeepLink(...deepLink.value)
            })
          )
        )
        .with({ isLogin: false, isDeepLink: true, isAuthRoute: false }, () =>
          appBridgeProvider((bridge: Bridge) => bridge.goDeepLink(...deepLink.value))
        )
        .with({ isLogin: false, isDeepLink: false, isAuthRoute: true }, () =>
          appBridgeProvider((bridge: Bridge) =>
            bridge.login().then(() => {
              bridgeLogin(window.getToken())
              return push(targetLink)
            })
          )
        )
        .with({ isLogin: false, isDeepLink: false, isAuthRoute: false }, () => push(targetLink))
        .exhaustive()
    } else {
      match({ isLogin, isAuthRoute })
        .with({ isLogin: true }, () => push(targetLink))
        .with({ isLogin: false, isAuthRoute: true }, () =>
          push({
            pathname: ROUTES.ACCOUNTS.LOGIN,
            query: {
              [QueryStringKeyEnum.ReturnUrl]: returnUrl,
            },
          })
        )
        .with({ isLogin: false, isAuthRoute: false }, () => push(targetLink))
        .exhaustive()
    }
  }

  const options = {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element) {
        // div#roulette-section 처리
        if (domNode.name === 'div' && domNode.attribs && domNode.attribs.id === 'roulette-section') {
          return <RouletteContent slug={ad.slug ?? ''} />
        }
        // img 처리
        if (domNode.name === 'img') {
          return <img src={domNode.attribs.src} alt={domNode.attribs.alt} style={{ width: '100%', display: 'block' }} />
        }
      }
    },
  }

  const cleanDescription = useMemo(() => {
    if (!ad.description) return ''

    return ad.description
      .replaceAll('&nbsp;', ' ') // non-breaking space를 일반 공백으로 변환
      .replace(/\s+/g, ' ') // 연속된 공백을 하나로 줄임
      .trim() // 앞뒤 공백 제거
  }, [ad.description])

  return (
    <StyledContainer onClick={handleClick}>{cleanDescription && parser(cleanDescription, options)}</StyledContainer>
  )
}

const StyledContainer = styled.section`
  p {
    margin-bottom: 0; /* global.less.css에서 기본값으로 1em을 주고 있어서 0으로 처리*/
  }

  /** a tag 자식으로 img가 있는 경우, a tag가 이미지영역 클릭시에 onClick이 되어야하므로 block으로 처리 */
  a {
    display: block;
  }
`

export default EventDetailDescription
